<script setup lang="ts">
import { rankingService } from '@features/rankings/api/rankings.service';
import { ref } from 'vue';
import AuthInput from '@features/auth/components/AuthInput.vue';
import { handleAxiosError } from '@shared/utils/error';
import { toast } from '@shared/utils/toast';

const newPassword = ref('');
const confirmNewPassword = ref('');
const passwordsError = ref(false);
const passwordsErrorMsg = ref('');
const passwordIsChanged = ref(false);
const titleFrom = ref({
  title: 'Create your Gaming Passport',
  titlePassword: 'New password',
  subtTitlePassword: 'Repeat password',
});
const viewPassword = ref(true);

interface InformationEdit {
  title?: string;
  titlePassword?: string;
  subtTitlePassword?: string;
}

interface Props {
  isEdit?: boolean;
  informationEdit?: InformationEdit;
  modelValue?: boolean;
  onNextStepSecondButton?: () => void;
  isWelcomePasswordModal?: boolean;
  isLoadingButton?: boolean;
}

const props = defineProps<Props>();

const emits = defineEmits(['update:modelValue', 'update:isLoadingButton']);

const clearPasswordErrors = () => {
  passwordsError.value = false;
  passwordsErrorMsg.value = '';
};

const validatePasswords = () => {
  if (newPassword.value !== confirmNewPassword.value) {
    passwordsError.value = true;
    passwordsErrorMsg.value = 'Passwords must match';
    return false;
  }

  const passwordRegex = /^(?=.*[A-Z])(?=.*\d).{12,}$/;
  if (!passwordRegex.test(newPassword.value)) {
    passwordsError.value = true;
    passwordsErrorMsg.value =
      'Minimum 12 characters, 1 capital letter and 1 digit';
    return false;
  }
  return true;
};

const changePassword = async () => {
  if (!validatePasswords()) return;

  try {
    emits('update:isLoadingButton', true);
    await rankingService.postChangePasswordRanking(
      newPassword.value,
      confirmNewPassword.value,
    );

    if (props.isWelcomePasswordModal) {
      toast.success({
        title: 'Create password',
        message: 'Your password has successfully created.',
      });
      emits('update:isLoadingButton', false);
      props?.onNextStepSecondButton?.();
      return;
    }

    emits('update:modelValue', false);
    passwordIsChanged.value = true;
    toast.success({
      title: 'Create password',
      message: 'Your password has successfully created.',
    });
    emits('update:isLoadingButton', false);
    return;
  } catch (error) {
    const { message } = handleAxiosError(error);
    if (message === 'User already exists') {
      passwordIsChanged.value = true;
    }
    passwordsError.value = true;
    passwordsErrorMsg.value = message;
    toast.error(message);
  } finally {
    emits('update:isLoadingButton', false);
  }
};

const editChangePassword = async () => {
  if (!validatePasswords()) return;

  try {
    emits('update:isLoadingButton', true);
    await rankingService.putChangePasswordRanking(
      newPassword.value,
      confirmNewPassword.value,
    );
    emits('update:modelValue', false);
    passwordIsChanged.value = true;
    toast.success({
      title: 'Password changed',
      message: 'Your password has been changed successfully.',
    });
  } catch (error) {
    handleAxiosError(error);
  } finally {
    emits('update:isLoadingButton', false);
  }
};

const onSubmitPassword = async () => {
  if (props.isEdit) {
    await editChangePassword();
  } else {
    await changePassword();
  }
};

const handleEnter = async () => {
  await onSubmitPassword();
};

defineExpose({ triggerSubmit: onSubmitPassword });
</script>

<template>
  <main class="h-full">
    <div class="flex size-full flex-col">
      <form
        class="flex size-full flex-col justify-between"
        @keydown.enter.prevent.stop="handleEnter"
        @submit.prevent="onSubmitPassword"
      >
        <div class="flex size-full flex-col justify-center gap-4">
          <p class="text-white">
            {{ props.isEdit ? props?.informationEdit?.title : titleFrom.title }}
          </p>
          <div class="flex flex-col gap-4">
            <AuthInput
              v-model="newPassword"
              :error="passwordsError"
              :title="
                props.isEdit
                  ? (props?.informationEdit?.titlePassword ?? '')
                  : titleFrom.titlePassword
              "
              input-type="password"
              label-class="text-white"
              :view-password="viewPassword"
              @clear-errors="clearPasswordErrors"
            />
            <AuthInput
              v-model="confirmNewPassword"
              :error="passwordsError"
              :title="
                props.isEdit
                  ? (props?.informationEdit?.subtTitlePassword ?? '')
                  : titleFrom?.subtTitlePassword
              "
              label-class="text-white"
              input-type="password"
              :error-message="passwordsErrorMsg"
              :view-password="viewPassword"
              @clear-errors="clearPasswordErrors"
            />
          </div>
        </div>
      </form>
    </div>
  </main>
</template>
